import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import PartnerBookingDetailsSidebar from '../../components/PartnerBookingDetailsSidebar';
import AuthService from '../../services/AuthService';
import '../admin/bookings/List.css';

const TABS = [
  { value: '',          label: 'All Bookings' },
  { value: 'pending',   label: 'Pending'      },
  { value: 'active',    label: 'Confirmed'    },
  { value: 'complete',  label: 'Completed'    },
  { value: 'cancelled', label: 'Cancelled'    },
];

const STATUS_OPTIONS = [
  { value: '',          label: 'All Status' },
  { value: 'pending',   label: 'Pending'    },
  { value: 'active',    label: 'Confirmed'  },
  { value: 'complete',  label: 'Completed'  },
  { value: 'cancelled', label: 'Cancelled'  },
];

const statusPillClass = (status) => {
  const map = {
    pending:   'status-pill status-pending',
    confirmed: 'status-pill status-confirmed',
    completed: 'status-pill status-completed',
    cancelled: 'status-pill status-cancelled',
    refunded:  'status-pill status-refunded',
    active:    'status-pill status-confirmed',
  };
  return map[status?.toLowerCase()] || 'status-pill status-default';
};

const PartnerBookings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoader, setIsLoader] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(searchParams.get('booking') || null);
  const [showBookingDetails, setShowBookingDetails] = useState(!!searchParams.get('booking'));
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1, per_page: 20, total_records: 0, total_pages: 0,
  });
  const [apiData, setApiData] = useState({
    search: '',
    status: searchParams.get('status') || '',
    page: 1,
  });

  useEffect(() => {
    if (!AuthService.getCurrentpartner()) { navigate('/partner/login'); return; }
  }, [navigate]);

  useEffect(() => {
    getBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData.page, apiData.status]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!e.target.closest('.booking-status')) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const getBookings = async () => {
    try {
      setIsLoader(true);
      const params = new URLSearchParams();
      if (apiData.status) params.append('status', apiData.status);
      params.append('page', apiData.page);
      params.append('limit', '20');

      const res = await ApiService.request({
        method: 'GET',
        url: `partner/getBookings?${params.toString()}`,
      });
      if (res.data.status) {
        setBookings(res.data.data || []);
        setPagination(res.data.pagination || {});
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Failed to fetch bookings');
    } finally {
      setIsLoader(false);
    }
  };

  const setStatus = (status) => {
    setApiData((p) => ({ ...p, status, page: 1 }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (status) next.set('status', status);
      else next.delete('status');
      return next;
    }, { replace: true });
  };

  const handleBookingClick = (booking) => {
    setSelectedBookingId(booking._id);
    setShowBookingDetails(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('booking', booking._id);
      return next;
    }, { replace: true });
  };

  const handleCloseOffcanvas = () => {
    setShowBookingDetails(false);
    setSelectedBookingId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('booking');
      return next;
    }, { replace: true });
  };

  const formatCurrency = (amount) => `AED ${Number(amount ?? 0).toLocaleString()}`;

  const serviceName = (b) => {
    const list = b.services || b.service_ids || [];
    if (!list.length) return '—';
    const first = list[0]?.title || list[0]?.name || '—';
    return list.length > 1 ? `${first} +${list.length - 1}` : first;
  };

  const formatDay = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const avatarUrl = (user) =>
    user?.picture || user?.image ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=F1E9DA&color=B98A4B&size=80`;

  const activeTab = (apiData.status || '').toLowerCase();
  const filteredBookings = apiData.search
    ? bookings.filter((b) => {
        const q = apiData.search.toLowerCase();
        return (
          (b.booking_code || '').toLowerCase().includes(q) ||
          (b.user?.name || '').toLowerCase().includes(q) ||
          serviceName(b).toLowerCase().includes(q)
        );
      })
    : bookings;

  const perPage = pagination.per_page || 20;
  const currentPage = pagination.current_page || 1;
  const totalRecords = pagination.total_records || 0;
  const fromEntry = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toEntry = Math.min(currentPage * perPage, totalRecords);

  const findStatusOption = (value) =>
    STATUS_OPTIONS.find((o) => o.value.toLowerCase() === String(value || '').toLowerCase());

  return (
    <>
      <div className="bookings-page">
        {/* Page Header */}
        <div className="bookings-page-header">
          <div>
            <h1 className="bookings-title">Bookings</h1>
            <div className="bookings-breadcrumb">Dashboard <i className="bi bi-chevron-right"></i> Bookings</div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bookings-card">
          {/* Tabs */}
          <div className="bookings-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.value || 'all'}
                className={`bookings-tab ${activeTab === tab.value.toLowerCase() ? 'active' : ''}`}
                onClick={() => setStatus(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="bookings-filters">
            <div className="bookings-search">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by booking ID, customer or service…"
                value={apiData.search}
                onChange={(e) => setApiData((p) => ({ ...p, search: e.target.value }))}
              />
            </div>

            <div className="booking-status">
              <button className="filter-select" onClick={() => setShowStatusDropdown((v) => !v)}>
                {findStatusOption(apiData.status)?.label || 'All Status'}
                <i className="bi bi-chevron-down"></i>
              </button>
              {showStatusDropdown && (
                <div className="status-dropdown">
                  {STATUS_OPTIONS.map((opt) => (
                    <div
                      key={opt.value || 'all'}
                      className={`status-dropdown-item ${activeTab === opt.value.toLowerCase() ? 'active' : ''}`}
                      onClick={() => { setStatus(opt.value); setShowStatusDropdown(false); }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Date</th>
                  <th className='max-w-150px'>Location</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoader && bookings.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <Loader text="Loading bookings…" size="30" />
                    </td>
                  </tr>
                ) : filteredBookings.length > 0 ? (
                  filteredBookings.map((b, i) => (
                    <tr key={b._id || i}>
                      <td className="booking-id-cell">
                        {b.booking_code || `#${b._id?.toString().slice(-6)}`}
                      </td>
                      <td>
                        <div className="customer-cell">
                          <img
                            className="customer-avatar"
                            src={avatarUrl(b.user)}
                            alt={b.user?.name || 'Customer'}
                            onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(b.user?.name || 'U')}&background=F1E9DA&color=B98A4B&size=80`; }}
                          />
                          <span className="customer-name">{b.user?.name || 'N/A'}</span>
                        </div>
                      </td>
                      <td>{serviceName(b)}</td>
                      <td>
                        <div className="date-cell">
                          <span className="date-day">{formatDay(b.event_date)}</span>
                          {b.event_time && <span className="date-time">{b.event_time}</span>}
                        </div>
                      </td>
                      <td>{b.address?.complete_address || 'N/A'}</td>
                      <td>
                        <span className={statusPillClass(b.status)}>
                          {(b.status || 'N/A').toUpperCase()}
                        </span>
                      </td>
                      <td className="amount-cell">{formatCurrency(b.grand_total)}</td>
                      <td>
                        <button
                          className="row-action-btn"
                          title="View details"
                          onClick={() => handleBookingClick(b)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div className="d-flex flex-column align-items-center">
                        <i className="bi bi-calendar-x fs-1 text-muted mb-3"></i>
                        <span className="text-muted">No bookings found</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          <div className="bookings-footer">
            <div className="entries-info">
              Showing {fromEntry} to {toEntry} of {totalRecords} entries
            </div>
            {pagination.total_pages > 1 && (
              <Paginator
                page={pagination.current_page}
                rows={pagination.per_page}
                totalRecords={pagination.total_records}
                onPageChange={(p) => setApiData((prev) => ({ ...prev, page: p }))}
              />
            )}
          </div>
        </div>
      </div>

      {/* Booking Details Offcanvas */}
      <div
        className={`offcanvas offcanvas-end booking-offcanvas ${showBookingDetails ? 'show' : ''}`}
        tabIndex="-1"
        style={{ visibility: showBookingDetails ? 'visible' : 'hidden', width: 460 }}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">Booking Details</h5>
          <button type="button" className="btn-close" onClick={handleCloseOffcanvas} aria-label="Close"></button>
        </div>
        <div className="offcanvas-body booking-offcanvas-body">
          {selectedBookingId && (
            <PartnerBookingDetailsSidebar
              bookingId={selectedBookingId}
              onUpdate={getBookings}
            />
          )}
        </div>
      </div>

      {showBookingDetails && (
        <div className="offcanvas-backdrop fade show" onClick={handleCloseOffcanvas}></div>
      )}
    </>
  );
};

export default PartnerBookings;
